import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import userManager from '../auth/oidcConfig';
import { clearSyncMarker } from '../auth/sessionSyncMarker';
import { navigateAway } from '../lib/tabCloseSuppression';

const AuthContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

// Phase 2 of the httpOnly-cookie migration (see backend/src/lib/authToken.js
// for the full plan) — this file used to read bescom_token out of
// localStorage and decode its payload locally to build `user`. Once the
// token moved into an httpOnly cookie (phase 1, already deployed), that's
// no longer possible even in principle: an httpOnly cookie is invisible to
// JavaScript by design, not just inconvenient to read. Every place that
// used to read/write/decode the token now instead asks the backend —
// GET /account/me for "who am I" (added in phase 1 specifically for this),
// credentials: 'include' everywhere instead of a manual Authorization
// header. The backend still accepts the OLD header too (phase 1's
// backward-compat fallback), but nothing here sends it anymore as of this
// change.

// Fetches /account/me and shapes it like oidc-client-ts's User (profile.*),
// so existing code (Dashboard.jsx etc.) that reads user.profile.X works
// identically regardless of which login path was used. Returns null for
// "no session" (401) as well as any network failure — both mean "we can't
// currently prove who this is", same treatment either way.
async function fetchMe() {
  try {
    const res = await fetch(`${API_BASE}/account/me`, { credentials: 'include' });
    if (!res.ok) return null;
    const data = await res.json();
    // loginAt: ADDED (2026-09-05) — powers Dashboard.jsx's "Session
    // Timer". See backend/src/lib/loginTime.js for why this is a
    // dedicated value, not derived from the JWT's own `iat` (which can
    // be refreshed independently of a real login).
    // mustChangePassword/needsMfaSetup/profileComplete: ADDED (2026-09-20) —
    // computed FRESH by the backend on every call (see accountUpdate.js's
    // /me header), not baked into the JWT. AccountCompletionGuard.jsx reads
    // these off `user` on every navigation to enforce them persistently —
    // the login/registration completion screens' own one-shot
    // navigate('/change-password') calls still fire immediately for a
    // smoother transition, this is the backstop that catches everything
    // else (escaping mid-flow, an admin resetting MFA later, etc).
    return {
      profile: data.profile,
      loginAt: data.loginAt,
      mustChangePassword: data.mustChangePassword,
      needsMfaSetup: data.needsMfaSetup,
      profileComplete: data.profileComplete,
      contactPhone: data.contactPhone,
      contactEmail: data.contactEmail,
    };
  } catch (_) {
    return null;
  }
}

// Best-effort refresh of the cookie itself (mints a fresh one with
// up-to-date claims — email/name/groups — from Authentik, picking up any
// admin-side changes since login). Failure here is fine either way: the
// cookie already in place keeps working, fetchMe() below just reads
// whatever it currently has.
async function refreshSessionCookie() {
  try {
    await fetch(`${API_BASE}/account/refresh-token`, { method: 'POST', credentials: 'include' });
  } catch (_) {
    // best-effort
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Mirrors `user` for the watchdog effect below, which needs the CURRENT
  // value inside a setInterval closure without re-subscribing the
  // interval on every single auth-state change.
  const userRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);

  // Pulled out of the mount-only effect below so it can also be called on
  // demand right after Register.jsx/Login.jsx/ClaimAccount.jsx complete.
  // Without this, `user` stays whatever it was resolved to when
  // AuthProvider first mounted (typically null, logged-out) for the rest
  // of the SPA session — a fresh cookie being set doesn't trigger a
  // re-render on its own, and previously this only ever got fixed by
  // accident, via a full-page reload that happened to also be part of the
  // old cross-app-SSO redirect.
  //
  // skipRefresh: passed by Register.jsx/Login.jsx/ClaimAccount.jsx right
  // after they complete — refreshing the cookie a moment later is not
  // just redundant (nothing could have gone stale in the milliseconds
  // since), it was also guaranteed to fail: /refresh-token depends on a
  // persisted Authentik session cookie that only exists once
  // SsoSyncCallback.jsx's /capture-session runs, which is itself LATER
  // than this point (the sso-session-sync round-trip Dashboard.jsx kicks
  // off after landing). Harmless either way — fetchMe() below still works
  // off the cookie that's already there — but skips a guaranteed-useless
  // round trip and the console noise it caused.
  // ChangePasswordRequired.jsx deliberately does NOT pass this — its
  // refreshUser() call happens well after the original login, so a real
  // refresh there is still meaningful.
  const resolveUser = useCallback(async ({ skipRefresh = false } = {}) => {
    const oidcUser = await userManager.getUser();
    if (oidcUser) {
      setUser(oidcUser);
      setLoading(false);
      return;
    }

    // No OIDC session — fall back to our custom-UI login's cookie. There's
    // no synchronous "is there even a token" check possible anymore (an
    // httpOnly cookie can't be read directly) — every path below has to
    // ask the server.

    // Check revocation BEFORE any refresh below — an already-revoked
    // session shouldn't get silently un-revoked-looking via a refresh
    // minting a fresh cookie with a newer `iat`. Also closes the gap
    // where a fresh page load/refresh right after a logout-elsewhere
    // previously wouldn't notice until the periodic watchdog's next tick,
    // up to 30s later — this runs the same check once at mount time too.
    // A 401 here just means "no session at all" (e.g. mid-registration,
    // or the public login page) — not a revocation, nothing to log out of.
    try {
      const statusRes = await fetch(`${API_BASE}/account/session-status`, { credentials: 'include' });
      if (statusRes.status === 401) {
        setUser(null);
        setLoading(false);
        return;
      }
      const statusData = await statusRes.json();
      if (statusData.valid === false) {
        // Falls back to a generic reason when the backend didn't give one
        // — true for a plain cross-app logout (Back-Channel Logout with
        // no admin action behind it), as opposed to 'rejected'/
        // 'deactivated' which always carry a specific reason. Previously
        // this branch silently set nothing for that case, so someone who
        // refreshed right after being logged out elsewhere saw no
        // explanation at all — see the watchdog effect below for the
        // same fix applied to the "already on a page" case.
        sessionStorage.setItem('bescom_logout_reason', statusData.reason || 'session-ended-elsewhere');
        // CONFIRMED BUG (2026-09-04): a session ending here (e.g. a
        // connected app's own logout revoking this session, not just this
        // app's own logout() below) genuinely kills the browser's
        // Authentik-side cookie too (see internalLogout.js's
        // /end-session), but previously left sessionSyncMarker's "recently
        // synced" marker in place from an earlier, now-dead sync. A
        // following fresh login then wrongly skipped its own real sync
        // (thinking one was still fresh), leaving the browser with no live
        // Authentik cookie — cross-app SSO to other apps then fell through
        // to Authentik's own raw interactive login instead of being
        // silent. Clear it here, at the moment we actually learn the
        // session is dead, not just on this app's own explicit logout().
        clearSyncMarker();
        setUser(null);
        setLoading(false);
        return;
      }
    } catch (_) {
      // Best-effort — a network blip shouldn't block login resolution;
      // fall through to the existing fetch-me logic below.
    }

    if (!skipRefresh) {
      await refreshSessionCookie();
    }

    const resolvedUser = await fetchMe();
    setUser(resolvedUser);
    setLoading(false);
  }, []);

  useEffect(() => {
    // CONFIRMED (2026-08-31): /sso-sync-callback is reached via a REAL
    // full-page navigation (through auth.bescomapps.com and back), not
    // client-side routing — so this whole app, AuthProvider included,
    // genuinely remounts there. That page's own effect (SsoSyncCallback.jsx)
    // is, at this exact moment, racing to call /capture-session — the
    // thing that makes /refresh-token succeed at all — so refreshing here
    // too was guaranteed to lose that race. Harmless (fetchMe() still
    // works off the cookie already there) but pure console noise. Skipped
    // ONLY on this one route — every other cold mount (someone opening
    // the app in a browser where they're already logged in from before)
    // still wants the refresh, to pick up admin-side changes since then.
    const skipRefresh = window.location.pathname === '/sso-sync-callback';
    resolveUser({ skipRefresh });

    userManager.events.addUserLoaded((u) => setUser(u));
    userManager.events.addUserUnloaded(() => setUser(null));

    return () => {
      userManager.events.removeUserLoaded();
      userManager.events.removeUserUnloaded();
    };
  }, [resolveUser]);

  // CONFIRMED BUG (2026-08-07): resolveUser() above only ever runs once,
  // on mount — so a session becoming invalid while someone just sits on a
  // page (no navigation, no fetch that happens to fail) left them looking
  // at stale data indefinitely; nothing re-checked validity until they
  // happened to trigger an API call or a fresh page load. A real hard
  // redirect (the same window.location.href logout() already uses) is
  // what actually gets every page, regardless of how each one checks its
  // own login state. Deliberately does NOT touch the OIDC (userManager)
  // path — that library already has its own token-expiry event handling.
  //
  // UPDATED (2026-08-11): also polls /account/session-status on the same
  // tick — a cheap local Redis lookup, not another Authentik round trip,
  // since backchannelLogout.js's receiver already did the real detection
  // work the moment a logout happened anywhere in the SSO ecosystem. This
  // is what catches "logged out via another app/tab, this tab never
  // refreshed" within one interval instead of only on this tab's own next
  // navigation.
  //
  // UPDATED (2026-08-31): gated on userRef.current being truthy — an
  // httpOnly cookie can't be locally checked for existence the way
  // localStorage.getItem('bescom_token') used to be, so without this
  // guard, EVERY tick on EVERY page (including someone mid-registration,
  // who has no bescom_token cookie at all yet, or an anonymous visitor on
  // the public login page) would hit session-status, get 401, and force a
  // hard redirect to '/' every 30 seconds — a real regression, not just
  // noise (mid-registration is a multi-minute, multi-step flow with no
  // session cookie until the very last one). Only watch for a session
  // GOING invalid once we actually believe one exists.
  useEffect(() => {
    async function checkSessionStatus() {
      if (!userRef.current) return;
      try {
        const res = await fetch(`${API_BASE}/account/session-status`, { credentials: 'include' });
        if (res.status === 401) {
          // Unlike resolveUser()'s own 401 branch (which also fires for
          // plain not-yet-logged-in states like mid-registration), this
          // only ever runs when userRef.current was already truthy — a
          // real session genuinely just disappeared. Same reasoning as the
          // data.valid === false branch below.
          clearSyncMarker();
          navigateAway('/');
          return;
        }
        const data = await res.json();
        if (data.valid === false) {
          // Same fallback as resolveUser()'s own session-status check
          // above — a plain cross-app logout (Back-Channel Logout with
          // no admin action behind it) carries no `reason` from the
          // backend, so this used to silently redirect with zero
          // explanation. 'session-ended-elsewhere' is what Login.jsx now
          // shows a message for.
          sessionStorage.setItem('bescom_logout_reason', data.reason || 'session-ended-elsewhere');
          // See the matching clearSyncMarker() call in resolveUser() above
          // — same reasoning, just the "already sitting on a page" path.
          clearSyncMarker();
          // ADDED (2026-09-16): this device's own bescom_token never gets
          // cleared by whatever ACTUALLY ended the session elsewhere (that
          // only touches Authentik's own sessions / this account's shared
          // revocation flag, never this specific browser's cookie) — so
          // without this, the Activity Log only ever showed the one device
          // that explicitly clicked Logout, with every OTHER device it
          // dragged down leaving no trace at all. Fire-and-forget: the
          // redirect below shouldn't wait on it, and a failure here (e.g.
          // this exact token having ALSO fully expired by now, not just
          // been revoked) just means one missing log row, not a broken
          // logout. Deliberately not added to the 401 branch above — that
          // one fires for a token that's invalid/expired on its own
          // terms, not specifically "revoked by something else", so
          // labeling it the same way would be misleading.
          fetch(`${API_BASE}/account/record-remote-logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
          navigateAway('/');
        }
      } catch (err) {
        // Best-effort — a network blip shouldn't log anyone out.
      }
    }

    // CONFIRMED (2026-09-11): a fixed 30s interval alone left a real gap —
    // logging out in one tab (e.g. Transfer App) doesn't touch this tab's
    // own bescom_token cookie at all (that logout only kills Authentik's
    // sessions server-to-server; this tab's own cookie stays "valid" by
    // its own clock until the next check catches up). Clicking an app
    // card from THIS tab inside that up-to-30s window still trusted the
    // stale sessionSyncMarker (clearSyncMarker() above hadn't run yet),
    // skipped the real re-sync, and let the target app's own real,
    // no-more-fallbacks signinRedirect() land on Authentik's raw
    // interactive login — exactly the "should never navigate to
    // auth.bescomapps.com" case this whole gateway exists to prevent.
    // Confirmed live: reproduced by logging out of Transfer App, then
    // immediately clicking another app card from this Dashboard tab.
    // visibilitychange closes the most common version of that gap —
    // returning to (or opening) this tab now triggers an immediate check
    // instead of waiting for whatever's left of the current 30s tick, same
    // pattern IdleTimeoutWatcher.jsx already uses for the same reason.
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') checkSessionStatus();
    }

    const interval = setInterval(checkSessionStatus, 30000);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, []);

  const login = () => userManager.signinRedirect();

  // reason: passed only by IdleTimeoutWatcher.jsx when the 15-minute idle
  // timeout actually fires the logout — a plain manual "Logout" click (or
  // any other caller) passes nothing. See accountUpdate.js's own
  // /end-session comment for how this becomes a label in the Activity Log.
  const logout = async (reason) => {
    // CONFIRMED (2026-08-11): signoutRedirect() — a browser redirect
    // through Authentik's own end-session endpoint — was the wrong tool
    // here. It only auto-returns afterward when the request carries an
    // id_token_hint, which needs a real prior signinRedirect() round trip
    // this app's own login never does (it drives Authentik's flow
    // executor directly instead) — so it either stranded people on
    // Authentik's own "Logout successful" page or, inconsistently, failed
    // outright ("the request is otherwise malformed"). Both exposed
    // auth.bescomapps.com's own UI, which this app exists specifically to
    // avoid. The provider is also configured for "Logout Method:
    // Back-channel", which is exactly what /account/end-session below
    // does instead — ends the Authentik session server-to-server, fully
    // invisible to the browser.
    //
    // CONFIRMED (2026-08-31): now AWAITED, unlike before — clearing the
    // httpOnly cookie can only happen via this response's Set-Cookie
    // actually being received (unlike the old localStorage.removeItem,
    // which was instant and local). Still feels just as fast: the backend
    // route now clears the cookie and responds immediately, running the
    // slow multi-session Authentik cleanup afterward server-side instead
    // of making this wait on it (see accountUpdate.js's own comment).
    // CONFIRMED BUG (2026-09-09): used to check plain truthiness of
    // `reason` — broke logout entirely for a caller that (accidentally)
    // passes a React SyntheticEvent as this argument (see TopBanner.jsx's
    // own comment on its onClick={logout} bug): JSON.stringify on an event
    // object throws (circular references), which the catch below silently
    // swallowed, so /account/end-session never actually ran. Checking for
    // the one real expected value means any other accidental argument is
    // just ignored, same as passing nothing at all — this can't silently
    // break the actual logout call again, whatever gets passed in here.
    const isIdle = reason === 'idle';
    try {
      await fetch(`${API_BASE}/account/end-session`, {
        method: 'POST',
        credentials: 'include',
        headers: isIdle ? { 'Content-Type': 'application/json' } : undefined,
        body: isIdle ? JSON.stringify({ reason }) : undefined,
      });
    } catch (e) {
      // Best-effort — still clear local state and leave below regardless
      // of whether the backend reached Authentik. Logged (not fully
      // silent) so a real failure here is at least visible for debugging —
      // this exact catch block is what hid the bug above until it was
      // tracked down by process of elimination.
      console.error('logout: /account/end-session call failed', e);
    }

    await userManager.removeUser();
    await userManager.clearStaleState();
    Object.keys(localStorage)
      .filter((k) => k.startsWith('oidc.'))
      .forEach((k) => localStorage.removeItem(k));

    // Safety measure for a shared computer: a stale marker here can't cause
    // impersonation (end-session above already killed the Authentik session
    // server-side, so a stale marker just means the NEXT login would skip a
    // sync it actually needed and fail cleanly on the next cross-app
    // authorize attempt, not succeed as the wrong user) — but there's no
    // reason to leave it around past a real logout either.
    clearSyncMarker();

    navigateAway('/');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser: resolveUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
